import React from "react";
import BlogTemplate from "./BlogTemplate";

// Import your images here (update paths as needed)
import ManageAdminDataImage from "../../assets/images/Blogs/Thumbnails/teamcenter-11-admin-data.jpg";
import AdminDataReportsImage from "../../assets/images/Blogs/TeamcenterAdminData/reports.png";
import EnvironmentCopyImage from "../../assets/images/Blogs/TeamcenterAdminData/env_copy.png";
import CompareDataImage from "../../assets/images/Blogs/TeamcenterAdminData/compare.png";
import ExportImportImage from "../../assets/images/Blogs/TeamcenterAdminData/export_import.png";
import MergeOptionsImage from "../../assets/images/Blogs/TeamcenterAdminData/merge_options.png";

const TeamcenterAdminData = () => {
  return (
    <BlogTemplate
      seoTitle="Siemens Teamcenter 11.2 Manages Deployments Efficiently | NexTech PLM"
      seoDescription="Learn how Siemens Teamcenter 11.2 helps you manage, export, import, synchronize, and compare admin data across multiple PLM environments."
      seoKeywords="Teamcenter 11.2, Admin Data, PLM Environments, Teamcenter Export Import, TEM"
      ogImage="https://nextechplm.com/og/teamcenter-admin-data.png"
      pageUrl="https://nextechplm.com/blog/siemens-teamcenter-11"
      bannerTitle="Managing Deployments Efficiently in Teamcenter 11.2"
    >
      <section className="container content-container">
        <div className="text-container">
          <p>
            Siemens Teamcenter 11.2 now enables us to manage and share admin data across the PLM environments efficiently. You can seamlessly Export, Import, Synchronize, Compare, and Analyze Admin Data.
          </p>
          <p>
            Today there is constant pressure to reduce time in managing and sharing Teamcenter administration data (such as AM rule tree, Organization, Preferences, Projects, Revision Rules, Saved Queries, Style Sheets, workflow, and so on) across multiple PLM environments like production, test, development, and integration. 
          </p>
        </div>
        <div className="image-container">
           <img src={ManageAdminDataImage} alt="Manage Administration Data UI in Teamcenter 11" />
        </div>
      </section>

      <section className="blog-section" style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <h2 className="sub-title">Core Goals and Requirements</h2>
        <p>This capability helps organizations achieve the following goals:</p>
        <ul className="blog-list">
          <li>Developing solutions for new business requirements and enhancing solutions for existing business requirements with reduced time and better quality.</li>
          <li>Performing upgrades which require building a replica for upgrade testing and several tests on configuration changes due to new versions with reduced time and better quality.</li>
          <li>Maintaining multiple environments for various activities such as ongoing development, testing, and integration.</li>
        </ul>
        
        <h3 className="key-element-title" style={{ marginTop: "2rem" }}>What the Tool Achieves</h3>
        <ul className="blog-list">
          <li>Copy admin data from one environment to another environment efficiently without using several utilities.</li>
          <li>Compare admin data between two environments.</li>
          <li>Perform analysis of admin data for an environment.</li>
          <li>Keep a record of what has been changed over the period in admin data to capture snapshots of configurations.</li>
        </ul>
        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={EnvironmentCopyImage} alt="Copy Admin Data from Environment to Environment" />
        </div>
        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "700px" }}>
           <img src={CompareDataImage} alt="Compare admin data between environments" />
        </div>
      </section>

      <section className="blog-section">
        <h2 className="sub-title">TEM Export / Import Capabilities</h2>
        <p>
          Siemens Teamcenter 11.2 introduces the "Manage Admin Data" capability, which helps you achieve all requirements using TEM or utilities, along with detailed reports.
        </p>

        <h3 className="key-element-title">Export Options</h3>
        <ul className="blog-list">
          <li>Exporting all admin data categories.</li>
          <li>Exporting selected categories.</li>
          <li>Exporting a partial set of elements from a category.</li>
        </ul>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "600px" }}>
           <img src={ExportImportImage} alt="Full Administration Data Export UI" />
        </div>

        <h3 className="key-element-title" style={{ marginTop: "2rem" }}>Import and Merge Options</h3>
        <p>
          The import tool will automatically merge the imported data into the target environment. You can select specific merge options for each category, including:
        </p>
        <ul className="blog-list">
          <li>Override with Source.</li>
          <li>Keep Target.</li>
          <li>Choose Latest.</li>
          <li>Choose source for unresolvable conflicts.</li>
          <li>Choose target for unresolvable conflicts.</li>
        </ul>
        <div className="image-container" style={{ margin: "1.5rem auto", width: "100%", maxWidth: "600px" }}>
           <img src={MergeOptionsImage} alt="Import Merge Options Configuration" />
        </div>
      </section>

      <section className="blog-section" style={{ backgroundColor: "#f0f8ff", borderRadius: "8px" }}>
        <h2 className="sub-title">Reporting and Command Line Utilities</h2>
        
        <h3 className="key-element-title">Administration Reports</h3>
        <p>A report gets generated after the import that shows how the target environment is impacted by the import. It also generates several other reports:</p>
        <ul className="blog-list">
          <li>Admin data report with all categories, specific categories, or a set of elements from a category to help you study admin data offline.</li>
          <li>Data import report detailing the impact on targeted environments.</li>
          <li>Compare report to help troubleshoot configuration differences between two environments to determine why the business logic is behaving differently.</li>
        </ul>
        <div className="image-container" style={{ margin: "2rem auto", width: "100%" }}>
           <img src={AdminDataReportsImage} alt="Admin Data Documentation, Comparison, and Import Reports" />
        </div>

        <h3 className="key-element-title">Command Line Utilities</h3>
        <p>You can also use command line utilities to export, import, and generate reports:</p>
        
        <h4 style={{ fontWeight: "600", marginTop: "1rem" }}>Export Admin Data</h4>
        <pre>
          <code>
            admin_data_export –adminDataTypes=All...<br/>
            admin_data_export –adminDataTypes=Preferences, RevisionRules...<br/>
            admin_data_export –partial –inputCriteria=EPMTaskTemplate &#123;template_name=MyTemplate&#125;<br/>
            admin_data_export –listTypes
          </code>
        </pre>

        <h4 style={{ fontWeight: "600", marginTop: "1rem" }}>Import Admin Data</h4>
        <pre>
          <code>
            admin_data_import –adminDataTypes=All...<br/>
            admin_data_import –adminDataTypes=Preferences, RevisionRules...<br/>
            admin_data_import –listTypes<br/>
            admin_data_import –dryrun
          </code>
        </pre>

        <h4 style={{ fontWeight: "600", marginTop: "1rem" }}>Generate Admin Data Report</h4>
        <pre>
          <code>
            generate_admin_data_report –adminDataTypes=All...<br/>
            generate_admin_data_report –adminDataTypes=Preferences, RevisionRules
          </code>
        </pre>

        <p className="text-bold" style={{ marginTop: "2rem" }}>
          Overall, Teamcenter has given us a way to manage and share admin data across multiple environments during various business situations where we need to export, import, synchronize, and analyze admin data.
        </p>
      </section>
    </BlogTemplate>
  );
};

export default TeamcenterAdminData;